import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WireframeLayout,
  WireframeHeader,
  WireframeContent,
  WireframeCard,
  WireframeButton,
  WireframeTable,
} from './WireframeLayout';
import { Search, Upload, FileText, MessageCircle, Globe, ChevronRight, ChevronLeft, RefreshCw, Copy, ThumbsUp, ThumbsDown, User, MapPin, Filter, ChevronDown, X, ExternalLink, Download } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TutorialStartButton } from './WireframeTutorial';

// ========== Location Data ==========
const locationData = {
  regions: [
    { id: 'east-africa', name: 'East Africa and Yemen', countries: ['ethiopia', 'kenya', 'somalia', 'yemen', 'south-sudan', 'uganda'] },
    { id: 'central-west-africa', name: 'Central and West Africa', countries: ['cameroon', 'car', 'drc', 'mali', 'niger', 'nigeria', 'burkina-faso'] },
    { id: 'middle-east', name: 'Middle East', countries: ['iraq', 'jordan', 'lebanon', 'syria', 'palestine'] },
    { id: 'asia', name: 'Asia', countries: ['afghanistan', 'myanmar', 'pakistan'] },
    { id: 'europe', name: 'Europe', countries: ['ukraine'] },
    { id: 'americas', name: 'Americas', countries: ['colombia', 'venezuela'] },
  ],
  countries: {
    'ethiopia': { name: 'Ethiopia', zones: ['somali', 'afar', 'oromia', 'tigray', 'amhara', 'snnp'] },
    'kenya': { name: 'Kenya', zones: ['turkana', 'garissa', 'wajir', 'mandera', 'nairobi'] },
    'somalia': { name: 'Somalia', zones: ['mogadishu', 'puntland', 'somaliland', 'jubaland'] },
    'yemen': { name: 'Yemen', zones: ['aden', 'sanaa', 'taiz', 'hodeidah'] },
    'south-sudan': { name: 'South Sudan', zones: ['juba', 'upper-nile', 'jonglei'] },
    'uganda': { name: 'Uganda', zones: ['kampala', 'west-nile', 'karamoja'] },
    'iraq': { name: 'Iraq', zones: ['baghdad', 'erbil', 'mosul', 'basra'] },
    'syria': { name: 'Syria', zones: ['damascus', 'aleppo', 'idlib', 'homs'] },
    'afghanistan': { name: 'Afghanistan', zones: ['kabul', 'herat', 'kandahar', 'mazar'] },
    'ukraine': { name: 'Ukraine', zones: ['kyiv', 'kharkiv', 'odesa', 'lviv'] },
    'colombia': { name: 'Colombia', zones: ['bogota', 'medellin', 'cali'] },
  },
  zones: {
    'somali': { name: 'Somali Region', woredas: ['jigjiga', 'gode', 'kebridehar', 'degehabur'] },
    'afar': { name: 'Afar Region', woredas: ['semera', 'dubti', 'aysaita', 'afambo'] },
    'oromia': { name: 'Oromia Region', woredas: ['adama', 'jimma', 'nekemte', 'shashemene'] },
    'tigray': { name: 'Tigray Region', woredas: ['mekelle', 'axum', 'adigrat', 'shire'] },
  }
};

// ========== Content Type Options ==========
const contentTypes = [
  { id: 'all', name: 'All Resources' },
  { id: 'protocol', name: 'Protocols' },
  { id: 'guideline', name: 'Guidelines' },
  { id: 'template', name: 'Templates' },
  { id: 'contact', name: 'Contacts' },
  { id: 'training', name: 'Training' },
];

// ========== Active Crises Data ==========
const activeCrises = [
  {
    id: 'cholera-somali',
    name: 'Cholera Outbreak',
    location: 'Somali Region, Ethiopia',
    severity: 'high',
    region: 'east-africa',
    country: 'ethiopia',
    zone: 'somali',
    resources: [
      { title: 'NRC Cholera Response Protocol', type: 'protocol', updated: 'Oct 2025', priority: true },
      { title: 'WHO Cholera Treatment Guidelines', type: 'guideline', updated: '2024 Edition' },
      { title: 'WASH in Cholera Outbreaks', type: 'guideline', updated: 'Sphere Standards' },
      { title: 'Cholera Rapid Assessment Tool', type: 'template', updated: 'Sep 2025' },
    ]
  },
  {
    id: 'flood-afar',
    name: 'Flood Emergency',
    location: 'Afar Region, Ethiopia',
    severity: 'moderate',
    region: 'east-africa',
    country: 'ethiopia',
    zone: 'afar',
    resources: [
      { title: 'Flood Early Warning SOP', type: 'protocol', updated: 'Ethiopia specific' },
      { title: 'Shelter & NFI Kit Standards', type: 'guideline', updated: 'NRC Global' },
      { title: 'Flood Damage Assessment Form', type: 'template', updated: 'Aug 2025' },
    ]
  },
  {
    id: 'drought-oromia',
    name: 'Drought Response',
    location: 'Oromia Region, Ethiopia',
    severity: 'moderate',
    region: 'east-africa',
    country: 'ethiopia',
    zone: 'oromia',
    resources: [
      { title: 'Drought Early Warning Protocol', type: 'protocol', updated: 'Jul 2025' },
      { title: 'Cash Transfer Guidelines', type: 'guideline', updated: 'Aug 2025' },
    ]
  }
];

// ========== Documents Data ==========
const allDocuments = [
  { id: 1, name: 'Emergency Response Activation Protocol', type: 'protocol', sector: 'Multi-sector', region: 'all', country: 'all', updated: 'Oct 2025', downloads: 234 },
  { id: 2, name: 'Rapid Assessment Tool - Ethiopia', type: 'template', sector: 'Multi-sector', region: 'east-africa', country: 'ethiopia', updated: 'Sep 2025', downloads: 189 },
  { id: 3, name: 'Cash Transfer Guidelines', type: 'guideline', sector: 'Cash', region: 'all', country: 'all', updated: 'Aug 2025', downloads: 156 },
  { id: 4, name: 'WASH Minimum Standards', type: 'guideline', sector: 'WASH', region: 'all', country: 'all', updated: 'Jul 2025', downloads: 312 },
  { id: 5, name: 'Beneficiary Registration Form', type: 'template', sector: 'Multi-sector', region: 'all', country: 'all', updated: 'Jun 2025', downloads: 445 },
  { id: 6, name: 'Coordination Meeting Template', type: 'template', sector: 'Coordination', region: 'all', country: 'all', updated: 'May 2025', downloads: 278 },
  { id: 7, name: 'Protection Monitoring Guidelines', type: 'guideline', sector: 'Protection', region: 'all', country: 'all', updated: 'Apr 2025', downloads: 198 },
  { id: 8, name: 'Somalia Context Analysis', type: 'guideline', sector: 'Multi-sector', region: 'east-africa', country: 'somalia', updated: 'Mar 2025', downloads: 145 },
  { id: 9, name: 'Ethiopia Emergency Protocols', type: 'protocol', sector: 'Multi-sector', region: 'east-africa', country: 'ethiopia', updated: 'Feb 2025', downloads: 267 },
  { id: 10, name: 'Field Staff Training Manual', type: 'training', sector: 'Multi-sector', region: 'all', country: 'all', updated: 'Jan 2025', downloads: 389 },
];

// ========== Contacts Data ==========
const allContacts = [
  { id: 1, name: 'Melese A.', org: 'NRC Ethiopia', role: 'Emergency Coordinator', region: 'east-africa', country: 'ethiopia', zone: 'all', phone: '+251-xxx', email: 'melese@nrc.no' },
  { id: 2, name: 'Dr. Sarah M.', org: 'WHO', role: 'Health Cluster Lead', region: 'east-africa', country: 'ethiopia', zone: 'somali', phone: '+251-xxx', email: 'sarah@who.int' },
  { id: 3, name: 'Ahmed H.', org: 'UNICEF', role: 'WASH Cluster Lead', region: 'east-africa', country: 'ethiopia', zone: 'somali', phone: '+251-xxx', email: 'ahmed@unicef.org' },
  { id: 4, name: 'Maria L.', org: 'WFP', role: 'Logistics Cluster Lead', region: 'east-africa', country: 'ethiopia', zone: 'all', phone: '+251-xxx', email: 'maria@wfp.org' },
  { id: 5, name: 'Regional EOC', org: 'Somali Region Govt', role: 'Govt. Coordination', region: 'east-africa', country: 'ethiopia', zone: 'somali', phone: '+251-xxx', email: 'eoc@somali.gov.et' },
  { id: 6, name: 'James K.', org: 'NRC Kenya', role: 'Country Director', region: 'east-africa', country: 'kenya', zone: 'all', phone: '+254-xxx', email: 'james@nrc.no' },
  { id: 7, name: 'Fatima A.', org: 'UNHCR', role: 'Protection Officer', region: 'east-africa', country: 'ethiopia', zone: 'afar', phone: '+251-xxx', email: 'fatima@unhcr.org' },
];

// ========== HumChat Component ==========
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  feedback?: 'positive' | 'negative' | null;
}

const welcomeMessage: Message = {
  id: 'welcome',
  role: 'assistant',
  content: `Hello! I'm **HumChat**, your AI assistant for humanitarian operations. I'm trained on IASC guidelines, Sphere standards, and Ethiopia-specific humanitarian context.

I can help you with:
- **Situation analysis** and crisis briefs
- **Response planning** and resource allocation
- **Protocol guidance** for emergencies
- **Coordination** support and communications

What humanitarian challenge can I help you address today?`,
  timestamp: new Date()
};

const exampleQuestions = [
  "Analyze displacement trends in Somali Region",
  "What are the priority interventions for health emergencies?",
  "Generate a rapid assessment plan for protection risks",
  "What coordination mechanisms should we establish?",
];

const generateResponse = (userMessage: string): string => {
  const lowerMessage = userMessage.toLowerCase();

  if (lowerMessage.includes('displacement') || lowerMessage.includes('trend') || lowerMessage.includes('analyze')) {
    return `## Displacement Analysis: Somali Region

### Current Situation
- **Total IDPs**: 1.2M (as of Jan 2026)
- **New displacements (30 days)**: 45,200
- **Primary drivers**: Drought (62%), Conflict (28%), Floods (10%)

### Key Trends
1. **Westward movement** toward urban centers increasing
2. **Secondary displacement** from overcrowded sites
3. **Cross-border movement** to Kenya decreasing

### Recommendations
- Expand site capacity in Jigjiga zone
- Pre-position NFI stock in Gode
- Strengthen protection monitoring`;
  }

  if (lowerMessage.includes('health') || lowerMessage.includes('priority') || lowerMessage.includes('intervention')) {
    return `## Priority Health Interventions

### Immediate Priorities (0-72 hours)
1. **Cholera response** - ORS distribution & treatment centers
2. **Maternal health** - Emergency obstetric care
3. **Malnutrition** - SAM/MAM screening & treatment

### Resource Requirements
- 50,000 ORS sachets
- 200 cholera treatment kits
- 15 additional health workers
- Mobile health teams (3 units)`;
  }

  if (lowerMessage.includes('assessment') || lowerMessage.includes('protection') || lowerMessage.includes('rapid')) {
    return `## Rapid Protection Assessment Plan

### Assessment Objectives
1. Identify protection risks and vulnerable groups
2. Map existing protection services
3. Assess community-based protection mechanisms

### Methodology
- **Duration**: 5 days
- **Team**: 4 protection officers, 2 interpreters
- **Data collection**: KIIs, FGDs, observation`;
  }

  if (lowerMessage.includes('coordination') || lowerMessage.includes('mechanism')) {
    return `## Coordination Mechanisms

### Recommended Structure
**Strategic Level**: Inter-cluster (Weekly), Govt liaison (Bi-weekly)
**Operational Level**: Sector working groups (Weekly)
**Field Level**: Site coordination (Daily)

### Key Stakeholders
| Organization | Role |
|--------------|------|
| OCHA | Coordination lead |
| NRC | Camp management |
| WHO | Health cluster |`;
  }

  return `Thank you for your question. I can help you with:

1. **Analysis** - Situation briefs, trend analysis
2. **Planning** - Response planning, resource allocation
3. **Protocols** - SOPs, guidelines
4. **Coordination** - Meeting briefs, communications

Please provide more details about the specific crisis or location.`;
};

function EmbeddedHumChat({ isExpanded, onToggle }: { isExpanded: boolean; onToggle: () => void }) {
  const [messages, setMessages] = useState<Message[]>([welcomeMessage]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = async (content: string) => {
    if (!content.trim()) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1500));

    const response = generateResponse(content);
    const assistantMessage: Message = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: response,
      timestamp: new Date()
    };

    setIsTyping(false);
    setMessages(prev => [...prev, assistantMessage]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const renderMarkdown = (content: string) => {
    let html = content
      .replace(/^### (.*$)/gim, '<h3 class="text-sm font-semibold text-gray-900 mt-3 mb-1">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-base font-semibold text-gray-900 mt-3 mb-2">$1</h2>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/^- (.*$)/gim, '<li class="ml-4 list-disc text-[13px]">$1</li>')
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 list-decimal text-[13px]">$1</li>')
      .replace(/\|/g, ' | ')
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n/g, '<br />');
    return <div dangerouslySetInnerHTML={{ __html: `<p class="mb-2">${html}</p>` }} />;
  };

  if (!isExpanded) {
    return (
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-50">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 bg-accent-500 text-white px-3 py-4 shadow-lg hover:bg-accent-600 transition-colors"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        >
          <MessageCircle className="w-4 h-4 rotate-90" />
          <span className="text-sm font-medium">HumChat AI</span>
          <ChevronLeft className="w-4 h-4 rotate-90" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-accent-500 flex items-center justify-center">
            <Globe className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">HumChat AI</h3>
            <p className="text-[10px] text-gray-500">Humanitarian AI Assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className="flex items-center gap-1 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 border border-green-200">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            Online
          </span>
          <button
            onClick={() => setMessages([welcomeMessage])}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            title="Clear chat"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onToggle}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            title="Collapse panel"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((message) => (
          <div key={message.id} className={cn('flex gap-2', message.role === 'user' ? 'flex-row-reverse' : '')}>
            <div className={cn('w-6 h-6 flex-shrink-0 flex items-center justify-center', message.role === 'assistant' ? 'bg-accent-100' : 'bg-gray-200')}>
              {message.role === 'assistant' ? <Globe className="w-3 h-3 text-accent-600" /> : <User className="w-3 h-3 text-gray-600" />}
            </div>
            <div className={cn('flex-1', message.role === 'user' ? 'text-right' : '')}>
              {message.role === 'assistant' && <div className="text-[10px] text-accent-600 font-medium mb-1">HAI</div>}
              <div className={cn('inline-block text-left px-3 py-2 text-[13px]', message.role === 'assistant' ? 'bg-gray-50 border border-gray-200' : 'bg-accent-500 text-white')}>
                {message.role === 'assistant' ? <div className="prose prose-sm max-w-none text-gray-700">{renderMarkdown(message.content)}</div> : <p className="whitespace-pre-wrap">{message.content}</p>}
              </div>
              {message.role === 'assistant' && (
                <div className="text-[9px] text-gray-400 mt-1">
                  {message.timestamp.toLocaleString([], { month: 'numeric', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex gap-2">
            <div className="w-6 h-6 bg-accent-100 flex items-center justify-center"><Globe className="w-3 h-3 text-accent-600" /></div>
            <div className="bg-gray-50 border border-gray-200 px-3 py-2">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-3 bg-gray-50">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about humanitarian response..."
            className="flex-1 px-3 py-2 border border-gray-200 bg-white text-[13px] outline-none focus:border-accent-500"
            disabled={isTyping}
          />
          <button
            onClick={() => handleSendMessage(inputValue)}
            disabled={!inputValue.trim() || isTyping}
            className={cn('px-4 py-2 text-sm font-medium transition-colors', inputValue.trim() && !isTyping ? 'bg-accent-500 text-white hover:bg-accent-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed')}
          >
            Send
          </button>
        </div>
        <div className="mt-2 space-y-1">
          {exampleQuestions.slice(0, 3).map((q, i) => (
            <button key={i} onClick={() => handleSendMessage(q)} className="block w-full text-left text-[11px] text-gray-500 hover:text-accent-500 truncate">"{q}"</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ========== Filter Dropdown Component ==========
function FilterDropdown({
  label,
  value,
  options,
  onChange,
  disabled = false,
  className = ''
}: {
  label: string;
  value: string;
  options: { id: string; name: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <label className="text-xs font-medium text-gray-600 whitespace-nowrap">{label}:</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={cn(
          'px-3 py-1.5 border border-gray-200 bg-white text-sm outline-none focus:border-accent-500 min-w-[140px]',
          disabled && 'bg-gray-100 text-gray-400 cursor-not-allowed'
        )}
      >
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>{opt.name}</option>
        ))}
      </select>
    </div>
  );
}

// ========== Main Knowledge Hub Component ==========
export function WireframeKnowledge() {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';

  // Notify parent window of current page for tooltip coordination
  useEffect(() => {
    if (isEmbedded && window.parent !== window) {
      window.parent.postMessage({ type: 'CLEAR_DEMO_PAGE', page: 'knowledge' }, '*');
    }
  }, [isEmbedded]);

  const [isChatExpanded, setIsChatExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Location filters
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedZone, setSelectedZone] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  // Get available countries based on selected region
  const availableCountries = useMemo(() => {
    if (selectedRegion === 'all') {
      return [{ id: 'all', name: 'All Countries' }];
    }
    const region = locationData.regions.find(r => r.id === selectedRegion);
    if (!region) return [{ id: 'all', name: 'All Countries' }];
    return [
      { id: 'all', name: 'All Countries' },
      ...region.countries.map(c => ({ id: c, name: locationData.countries[c]?.name || c }))
    ];
  }, [selectedRegion]);

  // Get available zones based on selected country
  const availableZones = useMemo(() => {
    if (selectedCountry === 'all') {
      return [{ id: 'all', name: 'All Zones' }];
    }
    const country = locationData.countries[selectedCountry];
    if (!country) return [{ id: 'all', name: 'All Zones' }];
    return [
      { id: 'all', name: 'All Zones' },
      ...country.zones.map(z => ({ id: z, name: locationData.zones[z]?.name || z }))
    ];
  }, [selectedCountry]);

  // Reset dependent filters when parent changes
  useEffect(() => {
    setSelectedCountry('all');
    setSelectedZone('all');
  }, [selectedRegion]);

  useEffect(() => {
    setSelectedZone('all');
  }, [selectedCountry]);

  // Filter crises based on location
  const filteredCrises = useMemo(() => {
    return activeCrises.filter(crisis => {
      if (selectedRegion !== 'all' && crisis.region !== selectedRegion) return false;
      if (selectedCountry !== 'all' && crisis.country !== selectedCountry) return false;
      if (selectedZone !== 'all' && crisis.zone !== selectedZone) return false;
      return true;
    });
  }, [selectedRegion, selectedCountry, selectedZone]);

  // Filter documents based on location and type
  const filteredDocuments = useMemo(() => {
    return allDocuments.filter(doc => {
      if (selectedRegion !== 'all' && doc.region !== 'all' && doc.region !== selectedRegion) return false;
      if (selectedCountry !== 'all' && doc.country !== 'all' && doc.country !== selectedCountry) return false;
      if (selectedType !== 'all' && doc.type !== selectedType) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!doc.name.toLowerCase().includes(query) && !doc.sector.toLowerCase().includes(query)) return false;
      }
      return true;
    });
  }, [selectedRegion, selectedCountry, selectedType, searchQuery]);

  // Filter contacts based on location
  const filteredContacts = useMemo(() => {
    return allContacts.filter(contact => {
      if (selectedRegion !== 'all' && contact.region !== selectedRegion) return false;
      if (selectedCountry !== 'all' && contact.country !== selectedCountry) return false;
      if (selectedZone !== 'all' && contact.zone !== 'all' && contact.zone !== selectedZone) return false;
      return true;
    });
  }, [selectedRegion, selectedCountry, selectedZone]);

  const clearFilters = () => {
    setSelectedRegion('all');
    setSelectedCountry('all');
    setSelectedZone('all');
    setSelectedType('all');
    setSearchQuery('');
  };

  const hasActiveFilters = selectedRegion !== 'all' || selectedCountry !== 'all' || selectedZone !== 'all' || selectedType !== 'all' || searchQuery;

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'protocol': return 'bg-red-100 text-red-700';
      case 'guideline': return 'bg-green-100 text-green-700';
      case 'template': return 'bg-blue-100 text-blue-700';
      case 'training': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <WireframeLayout embedded={isEmbedded}>
      <div className="flex h-full">
        {/* Main Content */}
        <div className={cn('flex-1 flex flex-col overflow-hidden transition-all duration-300', isChatExpanded ? 'mr-[380px]' : '')}>
          {!isEmbedded && <WireframeHeader
            title="Knowledge Hub"
            actions={
              <>
                <TutorialStartButton tutorialId="knowledge" variant="icon" />
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search documents..."
                    className="w-[200px] px-3 py-2 pl-9 border border-gray-200 bg-white text-[13px] outline-none focus:border-accent-500"
                  />
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
                <WireframeButton variant="primary" icon={<Upload className="w-4 h-4" />}>
                  Upload
                </WireframeButton>
              </>
            }
          />}

          <WireframeContent>
            {/* Location Filters Bar */}
            <div className="bg-white border border-gray-200 p-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-accent-500" />
                <span className="text-sm font-semibold text-gray-900">Filter by Location & Type</span>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-accent-500">
                    <X className="w-3 h-3" /> Clear filters
                  </button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <FilterDropdown
                  label="Region"
                  value={selectedRegion}
                  options={[{ id: 'all', name: 'All Regions' }, ...locationData.regions.map(r => ({ id: r.id, name: r.name }))]}
                  onChange={setSelectedRegion}
                />
                <FilterDropdown
                  label="Country"
                  value={selectedCountry}
                  options={availableCountries}
                  onChange={setSelectedCountry}
                  disabled={selectedRegion === 'all'}
                />
                <FilterDropdown
                  label="Zone"
                  value={selectedZone}
                  options={availableZones}
                  onChange={setSelectedZone}
                  disabled={selectedCountry === 'all'}
                />
                <div className="border-l border-gray-200 h-6 mx-2" />
                <FilterDropdown
                  label="Type"
                  value={selectedType}
                  options={contentTypes.map(t => ({ id: t.id, name: t.name }))}
                  onChange={setSelectedType}
                />
              </div>
            </div>

            {/* Active Crises Resources */}
            {filteredCrises.length > 0 && (
              <WireframeCard
                title="Active Crisis Resources"
                subtitle={`${filteredCrises.length} active ${filteredCrises.length === 1 ? 'crisis' : 'crises'} in selected location`}
                className="mb-6"
                noPadding
              >
                <div className="divide-y divide-gray-100">
                  {filteredCrises.map((crisis) => (
                    <div key={crisis.id} className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={cn('w-2 h-2', crisis.severity === 'high' ? 'bg-red-600' : 'bg-amber-500')} />
                        <span className="font-semibold text-gray-900">{crisis.name}</span>
                        <span className="text-xs text-gray-500">• {crisis.location}</span>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                        {crisis.resources.map((resource, idx) => (
                          <a
                            key={idx}
                            href="#"
                            className="flex items-center gap-2 p-2 bg-gray-50 hover:bg-gray-100 transition-colors group"
                          >
                            <FileText className={cn('w-4 h-4 flex-shrink-0', resource.priority ? 'text-accent-500' : 'text-gray-400')} />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-[12px] text-gray-900 truncate group-hover:text-accent-500">{resource.title}</div>
                              <div className="flex items-center gap-1">
                                <span className={cn('text-[10px] px-1.5 py-0.5', getTypeColor(resource.type))}>{resource.type}</span>
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </WireframeCard>
            )}

            {/* Two Column Layout: Documents + Contacts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Document Library - 2 columns */}
              <div className="lg:col-span-2">
                <WireframeCard
                  title="Document Library"
                  subtitle={`${filteredDocuments.length} documents found`}
                  noPadding
                >
                  <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="text-left px-4 py-3 font-medium text-gray-600">Document</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-600 w-24">Type</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-600 w-24">Sector</th>
                          <th className="text-left px-4 py-3 font-medium text-gray-600 w-24">Updated</th>
                          <th className="text-right px-4 py-3 font-medium text-gray-600 w-16"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDocuments.map((doc) => (
                          <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="font-medium">{doc.name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={cn('text-[11px] px-2 py-0.5', getTypeColor(doc.type))}>{doc.type}</span>
                            </td>
                            <td className="px-4 py-3 text-gray-600">{doc.sector}</td>
                            <td className="px-4 py-3 text-gray-500">{doc.updated}</td>
                            <td className="px-4 py-3 text-right">
                              <button className="text-accent-500 hover:text-accent-600">
                                <Download className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filteredDocuments.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                              No documents found matching your filters
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </WireframeCard>
              </div>

              {/* Key Contacts - 1 column */}
              <div>
                <WireframeCard
                  title="Key Contacts"
                  subtitle={`${filteredContacts.length} contacts`}
                  noPadding
                >
                  <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                    {filteredContacts.map((contact) => (
                      <div key={contact.id} className="p-3 hover:bg-gray-50">
                        <div className="font-medium text-sm text-gray-900">{contact.name}</div>
                        <div className="text-xs text-gray-500">{contact.org}</div>
                        <div className="text-xs text-accent-500 mt-1">{contact.role}</div>
                        <div className="flex gap-2 mt-2">
                          <a href={`mailto:${contact.email}`} className="text-xs text-gray-400 hover:text-accent-500">Email</a>
                          <span className="text-gray-300">•</span>
                          <a href={`tel:${contact.phone}`} className="text-xs text-gray-400 hover:text-accent-500">Call</a>
                        </div>
                      </div>
                    ))}
                    {filteredContacts.length === 0 && (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No contacts found for this location
                      </div>
                    )}
                  </div>
                </WireframeCard>
              </div>
            </div>
          </WireframeContent>
        </div>

        {/* HumChat Sidebar */}
        <div className={cn('fixed right-0 top-0 h-full z-40 transition-all duration-300', isChatExpanded ? 'w-[380px]' : 'w-0')}>
          <EmbeddedHumChat isExpanded={isChatExpanded} onToggle={() => setIsChatExpanded(!isChatExpanded)} />
        </div>
        {!isChatExpanded && <EmbeddedHumChat isExpanded={false} onToggle={() => setIsChatExpanded(true)} />}
      </div>
    </WireframeLayout>
  );
}

export default WireframeKnowledge;
